import React, { useState } from 'react';
import AppLayout from '@amzn/meridian/app-layout';
import Masthead, { MastheadTitle } from '@amzn/meridian/masthead';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Sheet from '@amzn/meridian/sheet';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import menuTokens from '@amzn/meridian-tokens/base/icon/menu';
import closeTokens from '@amzn/meridian-tokens/base/icon/close-small';
import documentTokens from '@amzn/meridian-tokens/base/icon/document';
import lockTokens from '@amzn/meridian-tokens/base/icon/lock';
import dashboardTokens from '@amzn/meridian-tokens/base/icon/dashboard';
import BrandMark from './BrandMark';
import { brand } from '../theme/brand';
import type { AppArea } from '../services/types';

/** Tab identifiers for the primary navigation. */
export type AppTab = 'asn' | 'promised' | 'leadtime';

const NAV_ITEMS: { id: AppTab; label: string }[] = [
    { id: 'asn', label: 'ASN' },
    { id: 'promised', label: 'Promised Date' },
    { id: 'leadtime', label: 'Lead Time' },
];

/** Presentation for each top-level area in the navigation drawer. */
const AREA_META: Record<
    AppArea,
    { label: string; description: string; icon: object; accent: string }
> = {
    connect: {
        label: 'Supplier Gateway',
        description: 'Submit ASNs, promised dates, and lead times',
        icon: documentTokens,
        accent: brand.teal,
    },
    control: {
        label: 'Supplier Control',
        description: 'Manage supplier access and approvals',
        icon: lockTokens,
        accent: '#6D28D9',
    },
    insights: {
        label: 'Supplier Insights',
        description: 'Performance reporting across suppliers',
        icon: dashboardTokens,
        accent: brand.blue,
    },
};

interface AppShellProps {
    children: React.ReactNode;
    userEmail?: string;
    /** Profile photo URL (federated employees); falls back to an initial. */
    userPicture?: string;
    onSignOut?: () => void;
    /** Optional actions rendered on the right of the masthead (e.g. guide). */
    navActions?: React.ReactNode;
    /** Active primary-nav tab. When provided, the tab row is shown. */
    tab?: AppTab;
    onTabChange?: (tab: AppTab) => void;
    /** Areas the user can access. A hamburger + left nav appears when > 1. */
    areas?: AppArea[];
    activeArea?: AppArea;
    onAreaChange?: (area: AppArea) => void;
}

interface HeaderProps {
    userEmail?: string;
    userPicture?: string;
    onSignOut?: () => void;
    navActions?: React.ReactNode;
    showMenu?: boolean;
    onOpenMenu?: () => void;
    /** Title shown next to the brand (the active workspace name). */
    title?: string;
}

/**
 * Top-right user avatar: the IdP profile photo when available (federated
 * employees), otherwise a circular initial derived from the email. The image
 * falls back to the initial if it fails to load.
 */
function UserAvatar({ email, picture }: { email: string; picture?: string }) {
    const [failed, setFailed] = useState(false);
    const initial = email.slice(0, 1).toUpperCase() || '?';

    const base: React.CSSProperties = {
        width: 30,
        height: 30,
        borderRadius: '50%',
        flexShrink: 0,
        objectFit: 'cover',
    };

    if (picture && !failed) {
        return (
            <img
                src={picture}
                alt=""
                referrerPolicy="no-referrer"
                onError={() => setFailed(true)}
                style={{ ...base, border: '1px solid rgba(0,0,0,0.12)' }}
            />
        );
    }

    return (
        <span
            aria-hidden="true"
            style={{
                ...base,
                background: brand.navy,
                color: '#fff',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 700,
            }}
        >
            {initial}
        </span>
    );
}

UserAvatar.defaultProps = {
    picture: undefined,
};

/**
 * Meridian Masthead: an optional hamburger + brand/app identity on the left;
 * guide, user, and sign out aligned to the right. Top-level area navigation
 * lives in a left slide-in drawer opened from the hamburger (see AppShell).
 *
 * Defined as a named component so AppLayout can identify it among its children
 * via the `headerComponent` prop.
 */
function AppHeader({
    userEmail,
    userPicture,
    onSignOut,
    navActions,
    showMenu,
    onOpenMenu,
    title,
}: HeaderProps) {
    return (
        <Masthead size="medium" backgroundColor="surface">
            <MastheadTitle>
                <Row alignmentVertical="center" spacing="300">
                    {showMenu ? (
                        <Button type="icon" label="Open navigation menu" onClick={onOpenMenu}>
                            <Icon tokens={menuTokens}>Menu</Icon>
                        </Button>
                    ) : null}
                    <BrandMark height={28} />
                    <div style={{ width: 1, height: 22, background: 'rgba(0,0,0,0.15)' }} />
                    <Text type="h300">{title}</Text>
                </Row>
            </MastheadTitle>

            {/* Right-aligned actions. */}
            {onSignOut ? navActions : null}
            {userEmail ? (
                <Row alignmentVertical="center" spacing="200">
                    <UserAvatar email={userEmail} picture={userPicture} />
                    <Text type="b200" color="secondary">
                        {userEmail}
                    </Text>
                </Row>
            ) : null}
            {onSignOut ? (
                <Button type="primary" size="small" onClick={onSignOut}>
                    Sign out
                </Button>
            ) : null}
        </Masthead>
    );
}

AppHeader.defaultProps = {
    userEmail: undefined,
    userPicture: undefined,
    onSignOut: undefined,
    navActions: undefined,
    showMenu: false,
    onOpenMenu: undefined,
    title: 'Supplier Gateway',
};

function AppFooter() {
    return (
        <Row alignmentHorizontal="center" spacingInset="300">
            <Text type="b100" color="secondary">
                SPIE — Supply Chain Product Innovation &amp; Engineering
            </Text>
        </Row>
    );
}

/** A rich, card-style nav row for one area in the drawer. */
function AreaNavItem({
    area,
    selected,
    onClick,
}: {
    area: AppArea;
    selected: boolean;
    onClick: () => void;
}) {
    const [hover, setHover] = useState(false);
    const meta = AREA_META[area];
    const active = selected || hover;

    let borderColor = 'transparent';
    if (selected) borderColor = meta.accent;
    else if (active) borderColor = brand.border;

    let background = 'transparent';
    if (selected) background = `${meta.accent}14`;
    else if (active) background = brand.pageBg;

    return (
        <button
            type="button"
            onClick={onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                width: '100%',
                textAlign: 'left',
                padding: '12px 14px',
                borderRadius: 10,
                border: `1px solid ${borderColor}`,
                borderLeft: `4px solid ${selected ? meta.accent : 'transparent'}`,
                cursor: 'pointer',
                background,
                transition: 'background 120ms ease, border-color 120ms ease',
                fontFamily: 'inherit',
            }}
        >
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: 9,
                    flexShrink: 0,
                    background: selected ? meta.accent : `${meta.accent}1F`,
                    color: selected ? '#fff' : meta.accent,
                }}
            >
                <Icon tokens={meta.icon}>{meta.label}</Icon>
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                <span
                    style={{
                        fontSize: 15,
                        fontWeight: selected ? 700 : 600,
                        color: selected ? meta.accent : brand.navy,
                    }}
                >
                    {meta.label}
                </span>
                <span style={{ fontSize: 12, color: brand.textMuted, lineHeight: 1.35 }}>
                    {meta.description}
                </span>
            </span>
        </button>
    );
}

/**
 * Branded application shell: a Masthead on top (with a hamburger that opens a
 * left area-navigation drawer for multi-area users), a primary-nav tab row
 * beneath it, full-width page content, and a footer. Header and footer are
 * passed as CHILDREN of AppLayout and identified via
 * headerComponent/footerComponent.
 */
export default function AppShell({
    children,
    userEmail,
    userPicture,
    onSignOut,
    navActions,
    tab,
    onTabChange,
    areas,
    activeArea,
    onAreaChange,
}: AppShellProps) {
    const [menuOpen, setMenuOpen] = useState(false);
    const multiArea = (areas?.length ?? 0) > 1;

    return (
        <AppLayout
            headerComponent={AppHeader}
            footerComponent={AppFooter}
            backgroundColor="alternateSecondary"
        >
            <AppHeader
                userEmail={userEmail}
                userPicture={userPicture}
                onSignOut={onSignOut}
                navActions={navActions}
                showMenu={multiArea}
                onOpenMenu={() => setMenuOpen(true)}
                title={activeArea ? AREA_META[activeArea].label : 'Supplier Gateway'}
            />
            <AppFooter />

            {/* Left area-navigation drawer (multi-area users). */}
            <Sheet
                type="overlay"
                side="start"
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                spacingInset="none"
            >
                <div
                    style={{
                        width: 320,
                        maxWidth: '100%',
                        minHeight: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        background: brand.surface,
                    }}
                >
                    {/* Branded header */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '18px 20px',
                            background: brand.navy,
                            color: '#fff',
                        }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span
                                style={{ fontSize: 15, fontWeight: 700, letterSpacing: '0.01em' }}
                            >
                                Supplier Gateway
                            </span>
                            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                                Choose a workspace
                            </span>
                        </div>
                        <Button
                            type="icon"
                            label="Close navigation menu"
                            onClick={() => setMenuOpen(false)}
                        >
                            <Icon tokens={closeTokens}>Close</Icon>
                        </Button>
                    </div>

                    {/* Area list */}
                    <div
                        style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            padding: 16,
                        }}
                    >
                        {(areas ?? []).map((a) => (
                            <AreaNavItem
                                key={a}
                                area={a}
                                selected={a === activeArea}
                                onClick={() => {
                                    onAreaChange?.(a);
                                    setMenuOpen(false);
                                }}
                            />
                        ))}
                    </div>

                    {/* Signed-in footer */}
                    {userEmail ? (
                        <div
                            style={{
                                borderTop: `1px solid ${brand.border}`,
                                padding: '14px 20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                            }}
                        >
                            <UserAvatar email={userEmail} picture={userPicture} />
                            <span
                                style={{
                                    fontSize: 12,
                                    color: brand.textMuted,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {userEmail}
                            </span>
                        </div>
                    ) : null}
                </div>
            </Sheet>

            <div style={{ width: '100%', boxSizing: 'border-box' }}>
                {/* Primary navigation on its own row, below the masthead. */}
                {tab ? (
                    <div
                        style={{
                            background: '#ffffff',
                            borderBottom: '1px solid rgba(0,0,0,0.12)',
                            padding: '0 24px',
                        }}
                    >
                        <TabGroup
                            value={tab}
                            onChange={(v: AppTab) => onTabChange?.(v)}
                            type="default"
                        >
                            {NAV_ITEMS.map((item) => (
                                <Tab key={item.id} value={item.id}>
                                    {item.label}
                                </Tab>
                            ))}
                        </TabGroup>
                    </div>
                ) : null}

                {/* Page content, titles pinned top-left. */}
                <div style={{ padding: '24px 24px', boxSizing: 'border-box' }}>{children}</div>
            </div>
        </AppLayout>
    );
}

AppShell.defaultProps = {
    userEmail: undefined,
    userPicture: undefined,
    onSignOut: undefined,
    navActions: undefined,
    tab: undefined,
    onTabChange: undefined,
    areas: undefined,
    activeArea: undefined,
    onAreaChange: undefined,
};
